// src/features/records/utils/mediaProcessing.js

/**
 * Media processing utilities for client-side operations
 * Handles temporary media preview generation
 */

/**
 * Generate a poster frame from a video file
 * Extracts a frame at 25% of video duration for representative thumbnail
 * 
 * @param {File} videoFile - Video file to process
 * @param {number} seekPercentage - Where to seek in video (0-1), defaults to 0.25
 * @returns {Promise<string>} Blob URL of the generated poster
 */
export async function generateVideoPoster(videoFile, seekPercentage = 0.25) {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    // Create object URL for the video
    const videoUrl = URL.createObjectURL(videoFile);
    
    // Clean up function
    const cleanup = () => {
      URL.revokeObjectURL(videoUrl);
      video.remove();
      canvas.remove();
    };
    
    video.onloadedmetadata = () => {
      // Seek to specified percentage of video duration
      video.currentTime = video.duration * seekPercentage;
    };
    
    video.onseeked = () => {
      try {
        // Set canvas dimensions to match video
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        
        // Draw the current frame to canvas
        ctx.drawImage(video, 0, 0);
        
        // Convert to blob
        canvas.toBlob((blob) => {
          if (blob) {
            const posterUrl = URL.createObjectURL(blob);
            cleanup();
            resolve(posterUrl);
          } else {
            cleanup();
            reject(new Error('Failed to generate poster blob'));
          }
        }, 'image/jpeg', 0.8);
      } catch (error) {
        cleanup();
        reject(error);
      }
    };
    
    video.onerror = (e) => {
      cleanup();
      reject(new Error('Failed to load video for poster generation'));
    };
    
    // Set video source
    video.src = videoUrl;
    video.load();
  });
}

/**
 * Clean up a blob URL when no longer needed
 * @param {string} blobUrl - Blob URL to revoke
 */
export function cleanupBlobUrl(blobUrl) {
  if (blobUrl && blobUrl.startsWith('blob:')) {
    URL.revokeObjectURL(blobUrl);
  }
}

/**
 * Generate a preview URL for a temporary attachment
 * For videos, generates a poster frame; for images, returns blob URL
 * 
 * @param {File} file - File to generate preview for
 * @param {string} type - Attachment type ('image', 'video', etc.)
 * @returns {Promise<{url: string, posterUrl?: string}>} Preview URLs
 */
export async function generateTempPreview(file, type) {
  if (type === 'video') {
    try {
      const posterUrl = await generateVideoPoster(file);
      return {
        url: URL.createObjectURL(file), // Keep original for potential playback
        posterUrl
      };
    } catch (error) {
      console.warn('Failed to generate video poster:', error);
      // Return just the video URL without poster
      return {
        url: URL.createObjectURL(file)
      };
    }
  } else if (type === 'image') {
    return {
      url: URL.createObjectURL(file)
    };
  } else {
    // For other file types, no preview needed
    return {};
  }
}